/**
 * Utility functions for quote operations
 * Pure functions that don't depend on React or external state
 */

/**
 * Format currency value
 */
export const formatCurrency = (value, options = {}) => {
  const { minimumFractionDigits = 0, maximumFractionDigits = 0, currency = 'USD' } = options;

  if (value == null || isNaN(value)) {
    return '$0';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits,
    maximumFractionDigits
  }).format(value);
};

/**
 * Format percentage value
 */
export const formatPercentage = (value, decimals = 2) => {
  if (value == null || isNaN(value)) {
    return '0%';
  }

  return `${parseFloat(value).toFixed(decimals)}%`;
};

/**
 * Calculate age from date of birth
 */
export const calculateAge = dateOfBirth => {
  if (!dateOfBirth) return null;

  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  return age;
};

/**
 * Validate date of birth
 */
export const validateDateOfBirth = dateOfBirth => {
  if (!dateOfBirth) {
    return { valid: false, error: 'Date of birth is required' };
  }

  const age = calculateAge(dateOfBirth);

  if (age < 0) {
    return { valid: false, error: 'Date of birth cannot be in the future' };
  }

  if (age < 18) {
    return { valid: false, error: 'Age must be at least 18' };
  }

  if (age > 120) {
    return { valid: false, error: 'Please enter a valid date of birth' };
  }

  return { valid: true, age };
};

/**
 * Sort quotes by a specific field
 */
export const sortQuotes = (quotes, sortBy, sortDirection = 'asc') => {
  if (!Array.isArray(quotes) || quotes.length === 0) {
    return quotes;
  }

  const sorted = [...quotes].sort((a, b) => {
    let aValue = a[sortBy];
    let bValue = b[sortBy];

    // Handle null/undefined values
    if (aValue == null) return 1;
    if (bValue == null) return -1;

    // Handle numeric values
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }

    // Handle string values
    const aStr = String(aValue).toLowerCase();
    const bStr = String(bValue).toLowerCase();

    if (sortDirection === 'asc') {
      return aStr.localeCompare(bStr);
    } else {
      return bStr.localeCompare(aStr);
    }
  });

  return sorted;
};

/**
 * Filter quotes by search term
 */
export const filterQuotesBySearch = (quotes, searchTerm) => {
  if (!searchTerm || searchTerm.trim() === '') {
    return quotes;
  }

  const term = searchTerm.toLowerCase();

  return quotes.filter(quote => {
    // Search in carrier name
    const carrierName = (quote.carrier_name || quote.carrier || '').toLowerCase();
    if (carrierName.includes(term)) return true;

    // Search in product name
    const productName = (quote.product_name || quote.name || '').toLowerCase();
    if (productName.includes(term)) return true;

    // Search in product type
    const productType = (quote.product_type || '').toLowerCase();
    if (productType.includes(term)) return true;

    return false;
  });
};

/**
 * Get unique carriers from quotes
 */
export const getUniqueCarriers = quotes => {
  const carriersMap = new Map();

  quotes.forEach(quote => {
    const carrierId = quote.carrier_id || quote.carrier;
    const carrierName = quote.carrier_name || quote.carrier || 'Unknown';

    if (!carriersMap.has(carrierId)) {
      carriersMap.set(carrierId, {
        id: carrierId,
        name: carrierName
      });
    }
  });

  return Array.from(carriersMap.values()).sort((a, b) => a.name.localeCompare(b.name));
};

/**
 * Get unique product types from quotes
 */
export const getUniqueProductTypes = quotes => {
  const productTypes = new Set();

  quotes.forEach(quote => {
    const productType = quote.product_type || quote.productType;
    if (productType) {
      productTypes.add(productType);
    }
  });

  return Array.from(productTypes).sort();
};

/**
 * Debounce function for performance optimization
 */
export const debounce = (func, wait = 300) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Deep clone an object
 */
export const deepClone = obj => {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  if (obj instanceof Date) {
    return new Date(obj.getTime());
  }

  if (obj instanceof Array) {
    return obj.map(item => deepClone(item));
  }

  if (obj instanceof Object) {
    const clonedObj = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        clonedObj[key] = deepClone(obj[key]);
      }
    }
    return clonedObj;
  }
};

/**
 * Check if two objects are deeply equal
 */
export const deepEqual = (obj1, obj2) => {
  if (obj1 === obj2) return true;

  if (obj1 == null || obj2 == null) return false;

  if (typeof obj1 !== 'object' || typeof obj2 !== 'object') return false;

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  for (const key of keys1) {
    if (!keys2.includes(key)) return false;
    if (!deepEqual(obj1[key], obj2[key])) return false;
  }

  return true;
};

/**
 * Generate a unique ID
 */
export const generateId = () => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// ─── FireLight action-type constants ─────────────────────────────────────────
// Mirrors the arrays defined in alq/QuoteActionModal.js and
// annuities_quoter/QuoteActionModal.js so that button-enable logic is
// always in sync with what those modals actually render.
const FL_DISABLED_ACTION_TYPES = ['applicant', 'firelight', 'psg_eapp'];

/**
 * Determine whether a quote is a Term product.
 *
 * Preferred source is the authoritative product lists returned by the
 * bga_quotes API. Term includes both standard term (e.g. "1 Year Term",
 * "10 Year Term") and Return-of-Premium term (e.g. "20 Year Term ROP",
 * "To age 65 (ROP)"). When a `termProducts` list is provided we do an exact,
 * case-insensitive membership check. When it is not available we fall back to
 * the naming pattern used across the app (matches "N Year Term" as well as
 * Return-of-Premium variants).
 *
 * @param {string} productType       - quote.product_type
 * @param {string[]} [termProducts]  - authoritative list of term product types
 *                                     (should include ROP products)
 * @returns {boolean}
 */
export const isTermProduct = (productType, termProducts) => {
  if (!productType) return false;

  if (Array.isArray(termProducts) && termProducts.length > 0) {
    const target = String(productType).trim().toLowerCase();
    return termProducts.some(tp => String(tp).trim().toLowerCase() === target);
  }

  const productTypeStr = String(productType).toUpperCase();
  // Standard term ("N YEAR TERM", including "... TERM ROP") or age-based ROP.
  return productTypeStr.includes('YEAR TERM') || productTypeStr.includes('ROP');
};

/**
 * Determine whether a quote has at least one action that would actually be
 * rendered inside the QuoteActionModal for that quote.
 *
 * Runs the same filtering rules used by:
 *   - alq/QuoteActionModal.js  (life quotes)
 *   - annuities_quoter/QuoteActionModal.js  (annuity quotes)
 *
 * @param {object} quote        - The quote object (needs carrier_id, product_id,
 *                                face_amount, guid)
 * @param {object} options
 * @param {string} options.quoteType   - 'life' | 'annuity'
 * @param {object} options.alqQuoter   - Life-quoter context object (custom_actions,
 *                                       embed_config_quote_actions)
 * @param {object} options.aaqQuoter   - Annuity-quoter context object (quote_actions,
 *                                       embed_config_quote_actions)
 * @param {string[]} [options.termProducts] - Authoritative list of term product
 *                                       types from the bga_quotes API. Used to
 *                                       gate the simplified E-Ticket action.
 * @returns {boolean}
 */
export const hasQuoteValidActions = (quote, { quoteType, alqQuoter, aaqQuoter, termProducts } = {}) => {
  if (!quote) return false;

  // Detect FireLight embed context — check all possible config locations used
  // across the different quoter contexts.
  const quoterConfig = (typeof window !== 'undefined' && (window.IXN_QUOTER_CONFIG || window.HEXURE_UQ_CONFIG || window.HEXURE_AQ_CONFIG)) || {};
  const isFireLight = quoterConfig.embed_context === 'fire_light';

  // Pick the right quoter object and actions based on quote type.
  // alq (life) stores custom actions under `custom_actions`.
  // aaq (annuity) stores them under `quote_actions`.
  const quoterObj = quoteType === 'life' ? alqQuoter : aaqQuoter;
  const customActions = quoterObj?.custom_actions || quoterObj?.quote_actions || [];

  // embed_config_quote_actions for the Universal Quoter is stored in the window
  // global (set by addEmbedConfigQuoteActions after life quotes are fetched).
  // That function processes QUOTER_CONFIG.quote_actions using should_show(quote)
  // per quote and produces items with enabled_quote_guids — this is the correct
  // source. HEXURE_UQ_CONFIG.quote_actions is the *raw* config (no enabled_quote_guids).
  // For standalone ALQ the field may live directly on alqQuoter, so that is used
  // as a fallback. Annuity (aaq) has no embed_config_quote_actions in UQ context,
  // which is correct — aaq actions are handled entirely via quote_actions (section 2).
  const embedConfigActions = (typeof window !== 'undefined' && window.UQ_EMBED_CONFIG_QUOTE_ACTIONS) || alqQuoter?.embed_config_quote_actions || [];

  // ── 1. embed_config_quote_actions ─────────────────────────────────────────
  // These are the FireLight-injected actions (fl_apply, fl_thirdPartyEticket…).
  // Logic mirrors both QuoteActionModal render blocks for embed_config_quote_actions.
  const hasValidEmbedAction =
    Array.isArray(embedConfigActions) &&
    embedConfigActions.some(action => {
      // Quote must be in the allowed list for this embed action.
      if (!action.enabled_quote_guids?.includes(quote.guid)) return false;

      // fl_thirdPartyEticket is the FireLight counterpart of the simplified
      // E-Ticket and is only supported for Term products (standard term +
      // Return-of-Premium) — mirrors alq/QuoteActionModal.js.
      if (action.type === 'fl_thirdPartyEticket') {
        if (!isTermProduct(quote.product_type, termProducts)) return false;

        // In FireLight, it is only shown when the carrier also has a
        // simplified_eticket custom action.
        if (isFireLight) {
          const hasSimplifiedEticketForCarrier = customActions.some(ca => ca.action_data?.activity === 'simplified_eticket' && ca.carrier_ids?.includes(quote.carrier_id));
          if (!hasSimplifiedEticketForCarrier) return false;
        }
      }

      return true;
    });

  if (hasValidEmbedAction) return true;

  // ── 2. custom_actions / quote_actions ──────────────────────────────────────
  // Logic mirrors the custom_actions rendering block in alq/QuoteActionModal and
  // the quoteActions rendering block in annuities_quoter/QuoteActionModal.
  const hasValidCustomAction =
    Array.isArray(customActions) &&
    customActions.some(action => {
      const carrier_ids = action.carrier_ids || [];
      const product_ids = action.product_ids || [];

      // Carrier must match.
      if (!carrier_ids.includes(quote.carrier_id)) return false;

      // Product must match (empty product_ids means "all products" for alq).
      if (product_ids.length && !product_ids.includes(quote.product_id)) return false;

      // Face-amount range check (relevant for life / alq actions).
      const min_face = action.min_face_amount || 0;
      const max_face = action.max_face_amount || 1_000_000_000;
      if (quote.face_amount != null && (quote.face_amount < min_face || quote.face_amount > max_face)) {
        return false;
      }

      // Simplified E-Ticket is only supported for Term products.
      // Uses the authoritative bga_quotes term_products list when available,
      // otherwise falls back to the "N Year Term" naming pattern.
      if (action.action_data?.activity === 'simplified_eticket' && !isTermProduct(quote.product_type, termProducts)) {
        return false;
      }

      // ── FireLight-specific filtering ──────────────────────────────────────
      if (isFireLight) {
        // annuities_quoter/QuoteActionModal: simplified_eticket is hidden in FL.
        // alq/QuoteActionModal also skips simplified_eticket in FL context.
        if (action.action_data?.activity === 'simplified_eticket') return false;

        // alq/QuoteActionModal._fireLightActionIsValid:
        // Disabled action types are never shown (unless it is a simplified_eticket
        // — already excluded above).
        if (FL_DISABLED_ACTION_TYPES.includes(action.action_type)) return false;
      }

      return true;
    });

  return hasValidCustomAction;
};

export default {
  formatCurrency,
  formatPercentage,
  calculateAge,
  validateDateOfBirth,
  sortQuotes,
  filterQuotesBySearch,
  getUniqueCarriers,
  getUniqueProductTypes,
  debounce,
  deepClone,
  deepEqual,
  generateId,
  hasQuoteValidActions,
  isTermProduct
};
